import { useCallback, useEffect, useRef, useState } from "react"
import {
  Animated,
  Platform,
  StyleSheet,
  Text,
  TouchableOpacity,
  useWindowDimensions,
  View,
} from "react-native"

import MaterialIcons from "@expo/vector-icons/MaterialIcons"
import * as NavigationBar from "expo-navigation-bar"
import { useFocusEffect, useLocalSearchParams } from "expo-router"
import * as ScreenOrientation from "expo-screen-orientation"
import { StatusBar } from "expo-status-bar"
import { useSafeAreaInsets } from "react-native-safe-area-context"

import { SidekickView } from "src/components/SidekickView"
import { WebView } from "src/components/WebView"
import { useMediaContext } from "src/context/MediaContextProvider"
import { useAppTheme } from "src/hooks/useAppTheme"
import { useStatusBarCompensation } from "src/hooks/useStatusBarCompensation"
import { backToRoot } from "src/utils/routerHelpers"

export default function FileViewerScreen() {
  const {
    url: urlParam,
    title: titleParam,
    resourceId,
    taskId,
  } = useLocalSearchParams<{
    url?: string | string[]
    title?: string | string[]
    resourceId?: string
    taskId?: string
  }>()
  const resolvedUrl = Array.isArray(urlParam) ? urlParam[0] : urlParam
  const resolvedTitle = Array.isArray(titleParam) ? titleParam[0] : titleParam
  const insets = useSafeAreaInsets()
  const { effectiveTopInset } = useStatusBarCompensation()
  const { theme, navTheme } = useAppTheme()
  const { colors } = theme
  const { width, height } = useWindowDimensions()
  const isLandscape = width > height

  const controlsOpacity = useRef(new Animated.Value(1)).current

  const [isLoading, setIsLoading] = useState(true)
  const [hasError, setHasError] = useState(false)
  const [isClosing, setIsClosing] = useState(false)

  const { setMediaContext, clearMediaContext } = useMediaContext()

  // Build the Google Docs Online viewer URL
  const viewerUrl = resolvedUrl
    ? `https://docs.google.com/gview?embedded=true&url=${encodeURIComponent(resolvedUrl)}`
    : ""

  // Unlock orientation for file viewer
  useFocusEffect(
    useCallback(() => {
      ScreenOrientation.unlockAsync().catch(() => {})
      // Don't re-lock on cleanup to prevent jitter when auto-navigating between media
    }, []),
  )

  // Register media context for context-aware chat responses
  // Note: Page tracking is not available with the Google Docs viewer
  useEffect(() => {
    if (resourceId) {
      setMediaContext({
        resourceId,
        taskId,
        title: resolvedTitle,
        initialOffset: "1", // Default to page 1 for documents
      })
    }
    return () => {
      clearMediaContext()
    }
  }, [resourceId, taskId, resolvedTitle, setMediaContext, clearMediaContext])

  // Handle Android navigation bar
  useEffect(() => {
    if (Platform.OS === "android") {
      NavigationBar.setVisibilityAsync("hidden")
      NavigationBar.setBehaviorAsync("overlay-swipe")
      NavigationBar.setBackgroundColorAsync("#000000")
      NavigationBar.setButtonStyleAsync("light")
    }

    return () => {
      if (Platform.OS === "android") {
        NavigationBar.setVisibilityAsync("visible")
        NavigationBar.setBackgroundColorAsync(theme.colors.background)
        NavigationBar.setButtonStyleAsync(navTheme.dark ? "light" : "dark")
      }
    }
  }, [theme.colors.background, navTheme.dark])

  // Handle WebView load completion
  const handleLoadEnd = useCallback(() => {
    setIsLoading(false)
  }, [])

  // Handle WebView errors
  const handleError = useCallback(() => {
    setIsLoading(false)
    setHasError(true)
  }, [])

  if (!resolvedUrl) {
    return (
      <View style={styles.errorContainer}>
        <MaterialIcons name="error-outline" size={48} color="#FFFFFF" />
        <Text style={styles.errorText}>No file URL provided.</Text>
        <TouchableOpacity style={styles.backButton} onPress={() => backToRoot()}>
          <Text style={styles.backButtonText}>Go Back</Text>
        </TouchableOpacity>
      </View>
    )
  }

  return (
    <View style={styles.container}>
      <StatusBar style="light" />

      {/* Close button - above SidekickView */}
      <View
        style={[
          styles.closeButton,
          {
            top: effectiveTopInset + 22,
            right: insets.right + 12,
          },
        ]}
      >
        <TouchableOpacity
          onPress={async () => {
            // Unmount AvatarOverlay if in landscape to prevent crash during orientation change (APP-738)
            if (isLandscape) {
              setIsClosing(true)
              // Wait for React to unmount the component
              await new Promise((resolve) => setTimeout(resolve, 0))
            }
            backToRoot()
          }}
          style={[styles.closeButtonInner, { backgroundColor: colors.buttonBackground }]}
          accessibilityRole="button"
          accessibilityLabel="Close file viewer"
        >
          <MaterialIcons name="close" size={20} color={colors.buttonText} />
        </TouchableOpacity>
      </View>

      {!isClosing && (
        <SidekickView
          showAvatar
          autoShowAvatarOnSpeech
          showCaptions
          showCCToggle
          showButtonCluster
          compactButtonCluster
          darkBackground={false}
          showControlsBarBackground={false}
          controlsOpacity={controlsOpacity}
          controlsVisible={true}
          riveOwner="video"
        >
          {/* File viewer content */}
          <View style={styles.webViewContainer}>
            {isLoading && (
              <View style={[styles.loadingContainer, { backgroundColor: colors.background }]}>
                <MaterialIcons name="hourglass-empty" size={32} color={colors.text} />
                <Text style={[styles.loadingText, { color: colors.text }]}>
                  Loading document...
                </Text>
              </View>
            )}
            {hasError && (
              <View style={styles.errorOverlay}>
                <MaterialIcons name="error-outline" size={48} color="#FFFFFF" />
                <Text style={styles.errorTitle}>Failed to Load Document</Text>
                <Text style={styles.errorText}>
                  The document could not be loaded. Please check that the file URL is publicly
                  accessible.
                </Text>
              </View>
            )}
            <WebView
              source={{ uri: viewerUrl }}
              style={[styles.webView, hasError && styles.hidden]}
              onLoadEnd={handleLoadEnd}
              onError={handleError}
              javaScriptEnabled
              domStorageEnabled
              startInLoadingState={false}
              scalesPageToFit
              allowsInlineMediaPlayback
              // Enable nested scrolling for proper touch handling in nested views
              nestedScrollEnabled
              // Disable third-party cookies to avoid OkHttp cookie parsing crash on Android
              thirdPartyCookiesEnabled={false}
              // Use hardware layer type on Android to enable hardware acceleration
              androidLayerType={Platform.OS === "android" ? "hardware" : undefined}
              // Set background color
              backgroundColor="#000000"
              // Handle errors gracefully
              onHttpError={(syntheticEvent) => {
                const { nativeEvent } = syntheticEvent
                console.warn("WebView HTTP error:", nativeEvent)
                setHasError(true)
              }}
            />
          </View>
        </SidekickView>
      )}
    </View>
  )
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: "#000000",
  },
  closeButton: {
    position: "absolute",
    zIndex: 2100,
  },
  closeButtonInner: {
    width: 36,
    height: 36,
    borderRadius: 18,
    justifyContent: "center",
    alignItems: "center",
  },
  errorContainer: {
    flex: 1,
    alignItems: "center",
    justifyContent: "center",
    padding: 24,
    backgroundColor: "#000000",
  },
  errorTitle: {
    fontSize: 18,
    fontWeight: "600",
    color: "#FFFFFF",
    marginTop: 16,
    marginBottom: 8,
    textAlign: "center",
  },
  errorText: {
    fontSize: 14,
    color: "#FFFFFF",
    textAlign: "center",
    marginBottom: 8,
  },
  backButton: {
    backgroundColor: "#007AFF",
    paddingHorizontal: 24,
    paddingVertical: 12,
    borderRadius: 8,
    marginTop: 16,
  },
  backButtonText: {
    color: "#FFFFFF",
    fontSize: 16,
    fontWeight: "600",
  },
  webViewContainer: {
    flex: 1,
  },
  webView: {
    flex: 1,
  },
  hidden: {
    opacity: 0,
  },
  loadingContainer: {
    ...StyleSheet.absoluteFillObject,
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#000000",
    zIndex: 1,
  },
  loadingText: {
    fontSize: 14,
    color: "#FFFFFF",
    marginTop: 12,
  },
  errorOverlay: {
    ...StyleSheet.absoluteFillObject,
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#000000",
    padding: 24,
    zIndex: 2,
  },
})
